import React, { useContext, useEffect, useRef, useState } from "react";
import { Text } from "rizzui/typography";
import CameraIcon from "../../../assets/camera";
import { Input } from "rizzui/input";
import { inputClassName, textAreaClassName } from "../../projects/new/first";
import { Controller, useForm } from "react-hook-form";
import { Textarea } from "rizzui/textarea";
import { emailValidationPattern } from "../../account/signup";
import CustomPhoneInput from "../../../component/phoneInput";
import CountryDropdown from "../../../component/countrySelect";
import { Button } from "rizzui/button";
import { Avatar } from "rizzui/avatar";
import { AuthContext } from "../../../context/authContext";
import BinIcon from "../../../assets/bin";
import { baseUrl } from "../../account/login";
import axios from "axios";
import countryList from "react-select-country-list";
import { useUpdateProfile } from "../../../hooks/useProfileDetails";
import { useNotifcication } from "../../../hooks/useNotification";
import { useQueryClient } from "@tanstack/react-query";
import cn from "../../../lib/utils/class-name";

export default function ProfilePage() {
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(
    undefined
  );
  const { openNotification } = useNotifcication();
  const { profile, token } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { mutate: updateProfile, status } = useUpdateProfile();
  const isLoading = status === "pending";
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
  } = useForm();

  const handleProfile = handleSubmit((data) => {
    const {
      first_name: firstname,
      last_name: lastname,
      email,
      business_name,
      phone,
      businessCountry,
      about_business: business_about,
    } = data;
    const business_country = countryList()
      .getData()
      .find(
        (country: { value: string }) => country.value === businessCountry?.code
      )?.label;
    const payLoad = {
      firstname,
      lastname,
      email,
      business_name,
      business_country,
      business_about,
    };
    updateProfile(payLoad, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["user_profile", token] });
        openNotification({ status: "success", message: "Profile updated" });
      },
      onError: (error) =>
        openNotification({
          status: "success",
          message: "Error updating profile",
        }),
    });
  });

  const titleClassName = "font-switzer font-[500] text-[#758599] text-[13px]";

  const handlePhoneChange = (newValue: string, countryCode: string) => {
    setValue("phone", newValue); // Update phone field
    setValue("businessCountry", { code: countryCode }); // Update businessCountry field
    setSelectedCountry(countryCode); // Update selectedCountry state
  };

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "businessCountry") {
        setSelectedCountry(value.businessCountry?.code || null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    const {
      firstname: first_name,
      lastname: last_name,
      email,
      business_name,
      business_about,
    } = profile ?? {};
    const phone = { phone: profile?.phone ?? undefined };
    const code = countryList()
      .getData()
      .find((country) => country.label === profile?.business_country)?.value;
    const businessCountry = {
      code,
    };
    setSelectedCountry(code || null);
    reset({
      first_name,
      last_name,
      email,
      business_name,
      phone: phone,
      about_business: business_about,
      businessCountry,
    });
  }, [profile]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleProfile();
      }}
      noValidate
      className="md:w-[513px] w-[96%] mx-auto md:mx-0 flex flex-col gap-[40px] my-[40px]"
    >
      <div className="flex justify-between flex-wrap">
        <Text className={titleClassName}>Profile photo</Text>
        <div className="w-[315px] flex justify-start">
          {profile?.firstname && (
            <ProfilePicture
              name={`${profile?.firstname} ${profile?.lastname}`}
              profileImage={""}
            />
          )}
        </div>
      </div>
      <div className="flex justify-between items-start flex-wrap">
        <Text className={titleClassName}>Name</Text>
        <div className="grid grid-cols-2 gap-[15px] w-full md:w-[315px]">
          <Input
            inputClassName={inputClassName}
            placeholder="First name"
            {...register("first_name", {
              required: { value: true, message: "Required" },
            })}
            error={
              typeof errors?.first_name?.message === "string"
                ? errors.first_name.message
                : ""
            }
          />
          <Input
            inputClassName={inputClassName}
            placeholder="Last name"
            {...register("last_name", {
              required: { value: true, message: "Required" },
            })}
            error={
              errors?.last_name?.message ? "" + errors.last_name.message : ""
            }
          />
        </div>
      </div>
      <div className="flex justify-between items-start flex-wrap">
        <Text className={titleClassName}>Business name</Text>
        <Input
          inputClassName={inputClassName}
          className="w-full md:w-[315px]"
          {...register("business_name", {
            required: { value: true, message: "Required" },
          })}
          error={
            errors?.business_name?.message
              ? "" + errors.business_name.message
              : ""
          }
        />
      </div>
      <div className="flex justify-between items-start flex-wrap">
        <Text className={titleClassName}>About your business</Text>
        <Textarea
          textareaClassName={textAreaClassName}
          className="w-full md:w-[315px]"
          {...register("about_business", {
            required: { value: true, message: "Required" },
          })}
          error={
            errors?.about_business?.message
              ? "" + errors.about_business.message
              : ""
          }
        />
      </div>
      <div className="flex justify-between items-start flex-wrap">
        <Text className={titleClassName}>Country of business</Text>
        <Controller
          control={control}
          name="businessCountry"
          rules={{
            required: {
              value: true,
              message: "Required",
            },
          }}
          render={({ field: { onChange, value, name } }) => (
            <div className="w-full md:w-[315px]">
              <CountryDropdown
                onChange={(code) => {
                  onChange({ code });
                }}
                value={value?.code}
                name={name}
              />
              {errors.businessCountry?.message && (
                <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                  {"" + errors.businessCountry.message}
                </p>
              )}
            </div>
          )}
        />
      </div>
      <div className="flex justify-between items-start flex-wrap">
        <Text className={titleClassName}>Phone number</Text>
        <Controller
          control={control}
          name="phone"
          rules={{
            required: {
              value: true,
              message: "Phone Number is Required",
            },
            validate: (value) => {
              if (!/^\d+$/.test(value)) {
                return "Enter a valid phone number";
              }
              if (value.length < 5) {
                return "Enter a valid phone number";
              }
              return true;
            },
          }}
          render={({ field: { onChange, value } }) => (
            <div className="w-full md:w-[315px]">
              <CustomPhoneInput
                onChange={({ phone, code }) => {
                  handlePhoneChange(phone, code); // Update phone and country
                }}
                value={value || ""}
                //selectedCountry={selectedCountry}
                disabled={true}
              />
              {errors.phone?.message && (
                <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                  {"" + errors.phone.message}
                </p>
              )}
            </div>
          )}
        />
      </div>
      <div className="flex justify-between items-start flex-wrap">
        <Text className={titleClassName}>Email address</Text>
        <Input
          type="email"
          disabled={true}
          inputClassName={`${inputClassName} !bg-transparent`}
          className=" w-full md:w-[315px]"
          {...register("email", { ...emailValidationPattern })}
          error={errors?.email?.message ? "" + errors?.email.message : ""}
        />
      </div>
      <div className="flex justify-end items-start flex-wrap">
        <Button
          className="!bg-btn w-full md:w-[315px] h-[40px] rounded-[4px] text-background font-switzer font-[500] text-[14px]"
          type="submit"
          isLoading={isLoading}
        >
          Save changes
        </Button>
      </div>
    </form>
  );
}

const ProfilePicture = ({ name, profileImage }) => {
  const [image, setImage] = useState<string | null>(profileImage);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { token } = useContext(AuthContext);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      const formData = new FormData();
      formData.append("profile_picture", file);
      await axios
        .put(
          `${baseUrl}/profile/update_profile_picture`,
          { formData },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then((response) => {
          console.log(response);
        })
        .catch((e) => {
          console.log(e);
        });
      //setImage(imageUrl);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="relative size-[70px]">
      <div className="group">
        <Avatar
          name={name}
          src={image ?? ""}
          customSize={70}
          className="text-[25px]"
        />
        <div
          className="absolute hidden group-hover:flex size-[70px] top-0 bg-black bg-opacity-40 rounded-full items-center justify-center cursor-pointer"
          onClick={triggerFileSelect}
        >
          <CameraIcon className="text-white size-[30px]" />
        </div>
      </div>
      <Button className="!bg-transparent !backdrop-blur-0 !blur-0 cursor-pointer absolute p-0 h-fit w-fit top-[51px] left-[52px]">
        <BinIcon className={"size-[20px]"} />
      </Button>
      <input
        type="file"
        accept="image/*"
        className="hidden"
        ref={fileInputRef}
        onChange={handleImageChange}
      />
    </div>
  );
};
